import React, { useState, useEffect } from 'react';
import axios from 'axios';
import FamilyTree from '../components/FamilyTree';
import AddMemberForm from '../components/AddMemberForm';

const CustomerDashboard = ({ user }) => {
  const [activeTab, setActiveTab] = useState('tree');
  const [members, setMembers] = useState([]);
  const [myRequests, setMyRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // POPUP STATE
  const [showForm, setShowForm] = useState(false);
  const [selectedNode, setSelectedNode] = useState(null);
  const [formMode, setFormMode] = useState('child');

  const userId = user?.id || 1; 
  const userEmail = user?.email || "customer@example.com"; 

  const fetchData = async () => {
      try {
        setLoading(true);
        // Fetch members and all requests
        const [membersRes, requestsRes] = await Promise.all([
           axios.get(`/api/family-members?email=${userEmail}`),
           axios.get(`/api/requests?userId=${userId}`) 
        ]);
        
        // Filter requests for just this user (if backend sends all)
        // Note: Ideally backend should filter, but this is safe for display
        const userRequests = requestsRes.data.filter(r => r.userId === userId);
        
        setMembers(membersRes.data);
        setMyRequests(userRequests);
      } catch(err) { console.error(err); } 
      finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, [userEmail, userId]);

  // Handler: Open Popup for Adding/Requesting
  const handleAddMember = (node, mode) => {
      setSelectedNode(node);
      setFormMode(mode || 'child');
      setShowForm(true);
  };

  // Handler: Request Phone Access
  const handleRequestPhone = async (targetMember) => {
      if(!window.confirm(`Request access to view ${targetMember.name}'s phone number?`)) return;

      try {
          await axios.post('/api/requests', {
              type: 'VIEW_PHONE',
              targetMemberId: targetMember.id,
              userId: userId
          });
          alert('Request Sent Successfully!');
          fetchData(); // Refresh list
      } catch (err) {
          console.error("Phone Request Error", err);
          alert('Failed to send request. ' + (err.response?.data?.error || err.message));
      }
  };

  const handleFormSubmit = async (data) => {
    try {
       await axios.post('/api/requests', {
           type: 'ADD_MEMBER',
           data: data,
           userId: userId,
           targetMemberId: selectedNode ? selectedNode.id : null
       });
       alert('Request sent to Admin for approval.');
       setShowForm(false);
       fetchData();
    } catch (err) {
       console.error(err);
       alert('Error sending request.');
    }
  };

  const handleDeleteRequest = async (id) => {
      if(!window.confirm("Cancel this request?")) return;
      try {
          alert("Request cancellation is processed by admin.");
      } catch(err) { alert("Error"); }
  };

  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-900 flex flex-col">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
              <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  My Family Portal
              </h1>
              <div className="flex gap-4">
                  <button onClick={() => setActiveTab('tree')} className={`px-4 py-2 rounded-full text-sm font-bold transition ${activeTab === 'tree' ? 'bg-blue-100 text-blue-700' : 'text-slate-500 hover:bg-slate-50'}`}>Family Tree</button>
                  <button onClick={() => setActiveTab('requests')} className={`px-4 py-2 rounded-full text-sm font-bold transition ${activeTab === 'requests' ? 'bg-purple-100 text-purple-700' : 'text-slate-500 hover:bg-slate-50'}`}>My Requests</button>
              </div>
          </div>
      </header>

      {/* Content */}
      <div className="flex-1 overflow-auto">
        {activeTab === 'tree' && (
            <div className="h-full">
                <FamilyTree 
                    members={members} 
                    onAddMember={handleAddMember} 
                    onRequestPhone={handleRequestPhone} 
                    isAdmin={false} 
                />
            </div>
        )}

        {activeTab === 'requests' && (
          <div className="max-w-4xl mx-auto p-8">
            <h2 className="text-2xl font-bold mb-6 text-slate-700">My Activity</h2>
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                <table className="w-full text-left">
                    <thead className="bg-slate-50 border-b border-slate-100 text-xs uppercase text-slate-500 font-bold">
                        <tr>
                            <th className="p-4">Type</th>
                            <th className="p-4">Details</th>
                            <th className="p-4">Status</th>
                            <th className="p-4">Admin Remark</th>
                            <th className="p-4">Action</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {myRequests.length === 0 ? (
                            <tr><td colSpan="5" className="p-8 text-center text-slate-400 italic">No requests found.</td></tr>
                        ) : myRequests.map(req => {
                            // FIXED: Display Name instead of ID
                            const targetName = req.TargetMember ? req.TargetMember.name : `Member ID ${req.targetMemberId}`;
                            
                            return (
                                <tr key={req.id} className="hover:bg-slate-50 transition">
                                    <td className="p-4 font-mono text-xs font-bold text-indigo-600 bg-indigo-50/50 rounded-r-lg">{req.type}</td>
                                    <td className="p-4 text-sm text-slate-700">
                                        {req.type === 'ADD_MEMBER' 
                                            ? `Add: ${req.data.name} (to ${targetName})` 
                                            : `View Phone: ${targetName}`
                                        }
                                    </td>
                                    <td className="p-4"><span className={`px-3 py-1 rounded-full text-xs font-bold ${req.status === 'APPROVED' ? 'bg-green-100 text-green-700' : req.status === 'REJECTED' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}`}>{req.status}</span></td>
                                    <td className="p-4 text-sm text-slate-500 italic">{req.adminRemark || '-'}</td>
                                    <td className="p-4">{req.status === 'pending' && <button onClick={() => handleDeleteRequest(req.id)} className="text-red-500 hover:text-red-700 font-bold text-xs">CANCEL</button>}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
          </div>
        )}
      </div>

      {/* UNIFIED POPUP FORM - REQUEST MODE */}
      {showForm && (
        <AddMemberForm 
            onSubmit={handleFormSubmit} 
            onClose={() => setShowForm(false)} 
            selectedNode={selectedNode}
            formMode={formMode}
            isRequestMode={true} 
        />
      )}
    </div>
  );
};

export default CustomerDashboard;